import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { chatActivity, qualityFlags } from '../api/client'
import PageHeader from '../components/PageHeader'
import { PageLayout, Skeleton } from '../components/ui'

function formatTime(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60))

  if (diffHrs < 1) return `${Math.max(1, Math.floor(diffMs / 60000))}m ago`
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays === 1) return 'Yesterday'
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function QualityAlerts({ days }) {
  const { data: flags, isLoading } = useQuery({
    queryKey: ['qualityFlags', days],
    queryFn: () => qualityFlags.get(days),
    refetchInterval: 120000,
  })

  const items = flags || []
  if (isLoading || items.length === 0) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
        <span className="text-xs font-mono tracking-wide text-amber-400 uppercase">
          Quality Alerts ({items.length})
        </span>
      </div>

      {items.slice(0, 10).map((flag) => {
        const reasons = (flag.flagNote || '').replace('AUTO: ', '').split('; ')
        return (
          <div
            key={flag.messageId}
            className="bg-dial-surface border border-amber-500/30 rounded-lg p-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-indices-bright truncate">
                    {flag.clientName || 'Unknown'}
                  </span>
                  {flag.corrected && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-lume-teal/20 text-lume-teal rounded font-mono">
                      CORRECTED
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {reasons.map((r, i) => (
                    <span
                      key={i}
                      className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded font-mono"
                    >
                      {r}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-indices-muted leading-relaxed line-clamp-2">
                  {flag.contentPreview}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[10px] text-indices-muted font-mono">
                  {formatTime(flag.flaggedAt)}
                </span>
                {flag.clientId && (
                  <Link
                    to={`/clients/${flag.clientId}`}
                    className="text-[10px] text-lume-teal hover:text-lume-green font-mono"
                  >
                    REVIEW
                  </Link>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function ChatActivityPage() {
  const [days, setDays] = useState(3)

  const { data: activity, isLoading } = useQuery({
    queryKey: ['chatActivity', days],
    queryFn: () => chatActivity.get(days),
    refetchInterval: 60000,
  })

  // Sort by most recent activity
  const sorted = (activity || []).slice().sort((a, b) =>
    new Date(b.lastActivity) - new Date(a.lastActivity)
  )

  const totalMessages = sorted.reduce((sum, c) => sum + c.messageCount, 0)

  return (
    <PageLayout>
      <PageHeader title="Chat Activity" showBack backTo="/" />

      <div className="p-4 space-y-4">
        {/* Period selector */}
        <div className="flex gap-2">
          {[1, 3, 7].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                days === d
                  ? 'bg-lume-teal text-dial-black'
                  : 'bg-dial-surface text-indices-medium border border-dial-border hover:bg-dial-elevated'
              }`}
            >
              {d === 1 ? 'Today' : `${d} Days`}
            </button>
          ))}
        </div>

        {/* Quality Alerts */}
        <QualityAlerts days={days} />

        {/* Summary */}
        <div className="flex items-center gap-4 py-2">
          <div className="text-sm text-indices-muted font-mono tracking-wide">
            {sorted.length} client{sorted.length !== 1 ? 's' : ''} active
          </div>
          <div className="text-sm text-indices-muted font-mono tracking-wide">
            {totalMessages} message{totalMessages !== 1 ? 's' : ''}
          </div>
        </div>

        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
        )}

        {!isLoading && sorted.length === 0 && (
          <div className="text-center py-12 text-indices-muted">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p>No client chat activity in this period</p>
          </div>
        )}

        {/* Client activity cards */}
        {sorted.map((client) => {
          const initials = `${client.firstName?.[0] || ''}${client.lastName?.[0] || ''}`.toUpperCase()
          return (
            <div key={client.clientId} className="bg-dial-surface border border-dial-border rounded-xl overflow-hidden">
              {/* Client header */}
              <div className="flex items-center gap-3 p-4 border-b border-dial-border">
                <div className="w-10 h-10 bg-dial-elevated text-lume-teal rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-indices-bright truncate">
                    {client.firstName} {client.lastName}
                  </div>
                  <div className="text-xs text-indices-muted font-mono tracking-wide">
                    {client.messageCount} msg{client.messageCount !== 1 ? 's' : ''} &middot; {client.conversationCount} conv{client.conversationCount !== 1 ? 's' : ''} &middot; {formatTime(client.lastActivity)}
                  </div>
                </div>
                <Link
                  to={`/clients/${client.clientId}`}
                  className="text-xs text-lume-teal hover:text-lume-green transition-colors font-mono tracking-wide"
                >
                  VIEW
                </Link>
              </div>

              {/* Recent questions */}
              {client.recentQuestions?.length > 0 && (
                <div className="p-3 space-y-2">
                  {client.recentQuestions.map((q, i) => (
                    <div key={i} className="flex gap-2">
                      <div className="w-1 bg-lume-teal/30 rounded-full flex-shrink-0 mt-1" style={{ minHeight: 16 }} />
                      <div className="min-w-0">
                        <p className="text-sm text-indices-medium leading-relaxed">{q.text}</p>
                        <p className="text-[10px] text-indices-muted font-mono mt-0.5">{formatTime(q.time)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </PageLayout>
  )
}
